import {FormControl} from '@angular/forms';
import {
  ComposeLineRow,
  composeLineRowFromPart,
  composeRowsToParts,
  isComposeParts,
} from './compose-field/compose-field.model';
import {
  completeLookupKeys, createLookupControls, isLookupParts, LookupControls, LookupDefinition, lookupDisplayName,
  lookupParts,
} from './lookup-controls';
import {parseTemplate, TemplatePart} from './value-template';

/**
 * Where a mapping row's value comes from. By default it is
 * `templateControl`, a `{Column}` template. With `composeControl` on it is
 * `composeRows` instead: ordered, prefixed source columns, each rendered
 * only when its cell has a value (see `compose-field/`). With
 * `lookupControl` on it is one column of the row a lookup finds (see
 * `LookupControls`). Compose and lookup each replace the template, so at
 * most one of them is on.
 */
export interface ValueSourceControls extends LookupControls {
  templateControl: FormControl<string>;
  composeControl: FormControl<boolean>;
  composeRows: ComposeLineRow[];
}

/** Fresh value-source controls on `template`, with compose and lookup
 * off. Turning either of those on turns the other off; the subscriptions
 * that do so reach only these controls and go with them. */
export function createValueSourceControls(template = ''): ValueSourceControls {
  const lookup = createLookupControls();
  const composeControl = new FormControl<boolean>(false, {nonNullable: true});
  composeControl.valueChanges.subscribe(on => {
    if (on && lookup.lookupControl.value) {
      lookup.lookupControl.setValue(false);
    }
  });
  lookup.lookupControl.valueChanges.subscribe(on => {
    if (on && composeControl.value) {
      composeControl.setValue(false);
    }
  });
  return {
    templateControl: new FormControl<string>(template, {nonNullable: true}),
    composeControl,
    composeRows: [],
    ...lookup,
  };
}

/** The row's value as `parts`: in compose mode one `conditional` part per
 * line with a column, in lookup mode the single `lookup` part (see
 * `lookupParts`), otherwise the parsed template. `columnNames` decides
 * which `{Column}` placeholders are columns. */
export function valueSourceParts(
  controls: ValueSourceControls, columnNames: ReadonlySet<string>, lookupKeys: ReadonlySet<string>,
): TemplatePart[] {
  if (controls.composeControl.value) {
    return composeRowsToParts(controls.composeRows);
  }
  return controls.lookupControl.value
    ? lookupParts(controls, lookupKeys)
    : parseTemplate(controls.templateControl.value, columnNames);
}

/** A row's "Hentes fra" choice: a `{Column}` template (`column`), a fixed
 * value (`constant`), one column of the row a lookup finds (`lookup`), or
 * composed lines (`compose`). */
export type ValueSourceKind = 'column' | 'constant' | 'lookup' | 'compose';

// The choices a row offers, by whether it offers a lookup and compose.
// One array per combination, so a template reads the same array on every
// check.
const TEMPLATE_KINDS: readonly ValueSourceKind[] = ['column', 'constant'];
const WITH_LOOKUP: readonly ValueSourceKind[] = ['column', 'constant', 'lookup'];
const WITH_COMPOSE: readonly ValueSourceKind[] = ['column', 'constant', 'compose'];
const ALL_KINDS: readonly ValueSourceKind[] = ['column', 'constant', 'lookup', 'compose'];

/** The choices a row offers: Kolonne and Fast verdi always, Kodeoppslag
 * and Sammensatt when `lookup` and `compose` say so. */
export function offeredValueSourceKinds(lookup: boolean, compose: boolean): readonly ValueSourceKind[] {
  if (lookup) {
    return compose ? ALL_KINDS : WITH_LOOKUP;
  }
  return compose ? WITH_COMPOSE : TEMPLATE_KINDS;
}

/** The choices a row reading `kind` offers: Kodeoppslag while
 * `lookupsOffered`, Sammensatt while `composable`, and each also on a
 * row that already reads it, so the select can show it. */
export function rowValueSourceKinds(
  kind: ValueSourceKind, lookupsOffered: boolean, composable: boolean,
): readonly ValueSourceKind[] {
  return offeredValueSourceKinds(lookupsOffered || kind === 'lookup', composable || kind === 'compose');
}

/** Each choice's name in the "Hentes fra" select. */
export const VALUE_SOURCE_KIND_LABELS: Record<ValueSourceKind, string> = {
  column: 'TRANS__IMPORT__VALUE_SOURCE__COLUMN',
  constant: 'TRANS__IMPORT__GROUP_CARD__FIXED_VALUE',
  lookup: 'TRANS__IMPORT__LOOKUP_TAG',
  compose: 'TRANS__IMPORT__VALUE_SOURCE__COMPOSE',
};

/** Each choice's explanation, shown on its option. */
export const VALUE_SOURCE_KIND_TOOLTIPS: Record<ValueSourceKind, string> = {
  column: 'TRANS__IMPORT__GROUP_CARD__FROM_COLUMN_TOOLTIP',
  constant: 'TRANS__IMPORT__GROUP_CARD__FIXED_VALUE_TOOLTIP',
  lookup: 'TRANS__IMPORT__LOOKUP_TOGGLE_TOOLTIP',
  compose: 'TRANS__IMPORT__COMPOSE_FIELD__TOGGLE_TOOLTIP',
};

/** The mode `valueSourceParts` reads: compose, else lookup, else the
 * template. */
export function valueSourceMode(controls: ValueSourceControls): 'template' | 'lookup' | 'compose' {
  if (controls.composeControl.value) {
    return 'compose';
  }
  return controls.lookupControl.value ? 'lookup' : 'template';
}

/** Turns lookup and compose mode on for a `kind` of that mode and off
 * otherwise; `column` and `constant` turn both off. Off goes first, so
 * turning one on never has to switch the other off. A control already
 * set right is left alone, so a switch between two kinds that read the
 * template changes no control. The inputs of a mode turned off keep their
 * values, unread. */
export function applyValueSourceKind(controls: ValueSourceControls, kind: ValueSourceKind): void {
  const lookup = kind === 'lookup';
  const compose = kind === 'compose';
  if (!lookup && controls.lookupControl.value) {
    controls.lookupControl.setValue(false);
  }
  if (!compose && controls.composeControl.value) {
    controls.composeControl.setValue(false);
  }
  if (lookup && !controls.lookupControl.value) {
    controls.lookupControl.setValue(true);
  }
  if (compose && !controls.composeControl.value) {
    controls.composeControl.setValue(true);
  }
}

/**
 * A flat mapping row's value source. Kolonne and Fast verdi both send the
 * row's template; `fixedValue` says which of the two edits it: the
 * `{Column}` template input, or a plain input for a fixed value. A switch
 * from Fast verdi to Kolonne changes no request body. A switch to Fast
 * verdi drops the template's columns and keeps its text
 * (`setFlatValueSourceKind`).
 */
export interface FlatValueSource extends ValueSourceControls {
  fixedValue: boolean;
}

export function flatValueSourceKind(row: FlatValueSource): ValueSourceKind {
  const mode = valueSourceMode(row);
  if (mode !== 'template') {
    return mode;
  }
  return row.fixedValue ? 'constant' : 'column';
}

/** Switches a flat row to `kind`. A switch to Fast verdi from another
 * choice leaves the template its literal text alone, without the
 * placeholders `columnNames` makes columns of, so a fixed value never
 * reads a column. */
export function setFlatValueSourceKind(
  row: FlatValueSource, kind: ValueSourceKind, columnNames: ReadonlySet<string>,
): void {
  const toFixedValue = kind === 'constant' && flatValueSourceKind(row) !== 'constant';
  row.fixedValue = kind === 'constant';
  applyValueSourceKind(row, kind);
  if (toFixedValue) {
    const text = templateText(row.templateControl.value, columnNames);
    if (text !== row.templateControl.value) {
      row.templateControl.setValue(text);
    }
  }
}

/** `template`'s literal text: every placeholder that names no column,
 * and the text between them. */
function templateText(template: string, columnNames: ReadonlySet<string>): string {
  return parseTemplate(template, columnNames).filter(part => part.kind === 'text').map(part => part.value).join('');
}

/** The column `template` reads when it is one bare `{Column}` and
 * nothing else, `null` otherwise: the one template a switch to Sammensatt
 * carries over losslessly, as its first line. */
export function bareTemplateColumn(template: string, columnNames: ReadonlySet<string>): string | null {
  const parts = parseTemplate(template, columnNames);
  return parts.length === 1 && parts[0].kind === 'column' ? parts[0].value : null;
}

/** The columns `template` reads, in order. */
export function templateColumns(template: string, columnNames: ReadonlySet<string>): string[] {
  return parseTemplate(template, columnNames).filter(part => part.kind === 'column').map(part => part.value);
}

/** Whether saved `parts` are a flat row's fixed value: literal text and
 * nothing else. */
export function isFixedValueParts(parts: TemplatePart[]): boolean {
  return parts.length > 0 && parts.every(part => part.kind === 'text');
}

/** Why a mapping line is marked incomplete: a translation key and its
 * parameters. */
export interface LineIssue {
  key: string;
  params?: { [name: string]: string };
}

/** What a row reading `kind` is marked incomplete for, `null` for
 * nothing: a Kodeoppslag or a Sammensatt whose parts (`lookupParts` over
 * the complete `lookups`, `composeRowsToParts`) are empty - it sends
 * nothing. `null` for Kolonne and Fast verdi. */
export function valueSourceIssue(
  controls: ValueSourceControls, kind: ValueSourceKind, lookups: readonly LookupDefinition[],
): LineIssue | null {
  if (kind === 'lookup') {
    return lookupParts(controls, completeLookupKeys(lookups)).length
      ? null : {key: 'TRANS__IMPORT__VALUE_SOURCE__LOOKUP_INCOMPLETE'};
  }
  if (kind === 'compose') {
    return composeRowsToParts(controls.composeRows).length
      ? null : {key: 'TRANS__IMPORT__VALUE_SOURCE__COMPOSE_EMPTY'};
  }
  return null;
}

/** One piece of a collapsed line's source: literal text, a source column,
 * a lookup by its name (blank for one with neither a name nor a table),
 * the lookup table's column whose text becomes the value (`lookupValue`),
 * the compose mark with `count` lines, or a compose line's label. */
export interface SourcePiece {
  kind: 'text' | 'column' | 'lookup' | 'lookupValue' | 'compose' | 'label';
  value: string;
  count?: number;
}

/**
 * What a collapsed line shows for a row reading `kind`: the template's
 * text and columns for Kolonne and Fast verdi; the lookup, the code
 * column it looks up and, once picked, the lookup table's value column
 * for Kodeoppslag; the compose mark and each line's
 * label and column for Sammensatt. A member's fixed pick is its own
 * control, not the template, and is left to the caller.
 */
export function valueSourceSummary(
  controls: ValueSourceControls, kind: ValueSourceKind, columnNames: ReadonlySet<string>,
  lookups: readonly LookupDefinition[],
): SourcePiece[] {
  if (kind === 'lookup') {
    const lookup = lookups.find(candidate => candidate.key === controls.lookupRefControl.value);
    const codeColumn = lookup?.codeColumnControl.value;
    const valueColumn = controls.lookupLabelControl.value;
    return [
      {kind: 'lookup', value: lookup ? lookupDisplayName(lookup) : ''},
      ...(codeColumn ? [{kind: 'column' as const, value: codeColumn}] : []),
      ...(valueColumn ? [{kind: 'lookupValue' as const, value: valueColumn}] : []),
    ];
  }
  if (kind === 'compose') {
    const lines = controls.composeRows.filter(line => !!line.columnControl.value);
    return [
      {kind: 'compose', value: '', count: lines.length},
      ...lines.flatMap(line => {
        const label = line.continueControl.value ? line.alonePrefixControl.value : line.prefixControl.value;
        return [
          ...(label ? [{kind: 'label' as const, value: label}] : []),
          {kind: 'column' as const, value: line.columnControl.value!},
        ];
      }),
    ];
  }
  return parseTemplate(controls.templateControl.value, columnNames)
    .map(part => ({kind: part.kind === 'column' ? 'column' : 'text', value: part.value}));
}

/** A collapsed Sammensatt source - a flat line's or a group card
 * member's - a row each: the compose mark on its first row, then its first
 * template lines' labels and columns, and how many lines are left out. */
export interface ComposeLinesSummary {
  mark: SourcePiece[];
  lines: SourcePiece[][];
  moreLines: number;
}

// A template of up to this many lines shows each; a longer one shows
// `COMPOSE_LINES_SHOWN_CAPPED` and a count of the rest.
const COMPOSE_LINES_SHOWN_WHOLE = 4;
const COMPOSE_LINES_SHOWN_CAPPED = 3;

/** A collapsed source's `pieces` (`valueSourceSummary`) split into a row
 * per template line, when they are a Sammensatt's with at least one line:
 * `null` otherwise, and the source keeps its one row. Each line is its
 * optional label and then its column, so a column piece ends a line. */
export function composeLinesSummary(pieces: readonly SourcePiece[]): ComposeLinesSummary | null {
  if (pieces[0]?.kind !== 'compose') {
    return null;
  }
  const lines: SourcePiece[][] = [];
  let line: SourcePiece[] = [];
  for (const piece of pieces.slice(1)) {
    line.push(piece);
    if (piece.kind === 'column') {
      lines.push(line);
      line = [];
    }
  }
  if (!lines.length) {
    return null;
  }
  const shown = lines.length > COMPOSE_LINES_SHOWN_WHOLE ? COMPOSE_LINES_SHOWN_CAPPED : lines.length;
  return {mark: [pieces[0]], lines: lines.slice(0, shown), moreLines: lines.length - shown};
}

/** What `restoreValueSource` needs from the page it restores onto. */
export interface ValueSourceRestoreContext {
  // The source's columns: a saved column outside it is reported.
  columnNames: ReadonlySet<string>;
  // The key a saved lookup was restored under, `null` for one that was not.
  restoredLookupKey: (savedKey: string) => string | null;
  nextComposeRowId: () => number;
  warn: (key: string, params?: any) => void;
}

/**
 * Put saved `parts` back on `controls`, the inverse of `valueSourceParts`:
 * a single `lookup` part turns lookup mode on, `conditional` parts turn
 * compose mode on with one line each, and anything else becomes the
 * template. A lookup that was not restored and a column the source lacks
 * are reported through `context.warn`.
 *
 * Returns the compose lines it created (`[]` in the other two modes). They
 * are not registered for change tracking: that is left to the caller.
 */
export function restoreValueSource(
  controls: ValueSourceControls, parts: TemplatePart[], context: ValueSourceRestoreContext,
): ComposeLineRow[] {
  if (isLookupParts(parts)) {
    restoreLookup(controls, parts[0], context);
    return [];
  }
  if (isComposeParts(parts)) {
    controls.composeControl.setValue(true);
    controls.composeRows = parts.map(part => {
      if (!context.columnNames.has(part.value)) {
        context.warn('TRANS__IMPORT__PROFILE__RESTORE__COLUMN_MISSING', {column: part.value});
      }
      return composeLineRowFromPart(context.nextComposeRowId(), part);
    });
    return controls.composeRows;
  }
  applyPartsToTemplate(controls.templateControl, parts, context.columnNames, context.warn);
  return [];
}

/** A saved `lookup` part back on lookup controls, naming the lookup by the
 * key it was restored under. The lookup is set before the value column,
 * since picking another lookup may reset the column. A part naming no
 * restored lookup leaves lookup mode on with none picked, and says so. */
function restoreLookup(controls: LookupControls, part: TemplatePart, context: ValueSourceRestoreContext): void {
  const key = context.restoredLookupKey(part.value);
  if (!key) {
    context.warn('TRANS__IMPORT__PROFILE__RESTORE__LOOKUP_MISSING');
  }
  controls.lookupControl.setValue(true);
  controls.lookupRefControl.setValue(key);
  controls.lookupLabelControl.setValue(key ? part.label_column ?? null : null);
}

/** Render `parts` back into a `{Column}` template on `control`. A column
 * outside `columnNames` stays in the template and is reported once, after
 * the template is set, naming the last such column. */
export function applyPartsToTemplate(
  control: FormControl<string>, parts: TemplatePart[], columnNames: ReadonlySet<string>,
  warn: ValueSourceRestoreContext['warn'],
): void {
  let missingColumn: string | null = null;
  const text = parts.map(part => {
    if (part.kind === 'text') {
      return part.value;
    }
    if (!columnNames.has(part.value)) {
      missingColumn = part.value;
    }
    return `{${part.value}}`;
  }).join('');
  control.setValue(text);
  if (missingColumn) {
    warn('TRANS__IMPORT__PROFILE__RESTORE__COLUMN_MISSING', {column: missingColumn});
  }
}
